/**
 * The padlock, and the pricing modal behind it.
 *
 * **Show the locked door, don't hide it.** Anything an agency cannot use because of its plan is
 * drawn anyway, with this mark on it, and pressing it says what would open it. The user's
 * reasoning, in their words: *"all limitations should be visible, but gated on pricing modal
 * (including items on the sidebar). That would encourage them to pay."*
 *
 * That is a reversal of what the product used to do, which was to make the option disappear. A
 * feature nobody can see is a feature nobody buys.
 *
 * ## One padlock, drawn once
 *
 * Everything shut in this application comes through `Padlock`, and `Padlock` draws exactly one
 * mark: the brand disc, the lock from the icon set, and — where there is room — a word. A padlock
 * drawn three ways is three padlocks, and a reader who has to learn each of them learns instead to
 * ignore all of them.
 *
 * **It must read as "there is more here", not as an error.** So: the brand colour rather than the
 * danger red that means a real failure everywhere else in the product, a filled disc rather than a
 * warning triangle, and a cursor that says it is pressable. Nothing here is broken and nothing has
 * been taken away — that sentence is the whole tone, and the copy inside the modal keeps it.
 *
 * ## It never decides anything
 *
 * Every one of these takes a `Gate` the server built. The message shown is the server's message,
 * the plan named is the plan the server named, and the plan offered is the one the server said
 * would open it. **Nothing in this file compares a plan code**, counts a limit, or works out what
 * somebody should buy — see `lib/gating.ts`, and `packages/core/src/plans.ts`, which is the only
 * file in the repository allowed to know any of that.
 *
 * ## And it is never drawn over a permission
 *
 * A caller reaches this only for something the person could otherwise do. The rule lives in
 * `useRecordLock` and is repeated everywhere it is applied, because getting it wrong fills staff
 * screens with padlocks nobody can open.
 */

import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useSession } from "@/lib/session";
import { useModuleLink } from "@/lib/module-rail";
import type { Gate } from "@/lib/gating";
import { IconLock } from "./icons";
import { Alert, Modal } from "./ui";

/**
 * Where the padlock is being drawn, which decides only its colours.
 *
 * Three tones and no more, and the third exists for one place. A padlock that takes a palette is a
 * padlock that stops looking like one mark, so all three draw the same lock at the same size and
 * differ only in what is behind it.
 *
 * - `light` — the default, and everything on a page: the brand chip.
 * - `dark` — a brand chip inverted, for a dark panel that has room for one.
 * - `rail` — **the navigation only.** Bare glyph, inheriting the item's own colour, for two
 *   reasons that both come from the rail being narrow and two-coloured. A chip took enough width
 *   to truncate "Import and export" to "Import and ex…", and the item somebody is *on* is a white
 *   pill, where a chip built out of `white/10` and `white/80` is white on white and vanishes at
 *   the exact moment the reader is looking straight at it. `text-current` follows the item into
 *   both states without the rail having to know which one it is in.
 */
type PadlockTone = "light" | "dark" | "rail";

const TONE: Record<PadlockTone, string> = {
  light: "px-1.5 py-0.5 border-brand-200 bg-brand-50 text-brand-700 hover:border-brand-300 hover:bg-brand-100",
  dark: "px-1.5 py-0.5 border-white/20 bg-white/10 text-white/80 hover:bg-white/20 hover:text-white",
  rail: "p-0.5 border-transparent bg-transparent text-current opacity-90 hover:opacity-100",
};

export function Padlock({
  gate,
  label,
  tone = "light",
  size = 13,
  className = "",
}: {
  /** The server's answer. Nothing is drawn without one, so a caller may pass a null straight in. */
  gate: Gate | null | undefined;
  /**
   * A word beside the mark, where there is room for one.
   *
   * Left off in a dense list, where the mark alone is the whole message and a column of the word
   * "Locked" repeated forty times is a column of noise.
   */
  label?: string;
  tone?: PadlockTone;
  size?: number;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!gate) return null;

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          /* A padlock is very often drawn *inside* a row that is itself a link. Without this, the
             press opens the record behind it and the modal is never seen — which is the one
             failure that would make the whole feature look broken. */
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
        }}
        title={gate.message}
        aria-label={`Locked — ${gate.message}`}
        data-padlock={gate.reason}
        data-padlock-subject={gate.subject}
        className={`inline-flex shrink-0 items-center gap-1 rounded-full border text-[11px] font-semibold transition-colors ${TONE[tone]} ${className}`}
      >
        <IconLock size={size} />
        {label ? <span>{label}</span> : null}
      </button>

      <PricingModal gate={gate} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/**
 * What is shut, what you are on, and what opens it.
 *
 * Four things, in this order, and the order is the argument:
 *
 * 1. **The server's own sentence**, verbatim. It already says what is shut, what to do about it,
 *    and — the part that stops a refusal being frightening — that nothing has been lost and the
 *    money is still moving. Re-writing it here would be a second account of the same rule, and the
 *    two would drift.
 * 2. **The plan they are on, beside the plan that opens it.** Named, never compared: the server
 *    put both names in the gate precisely so that no screen has to work out which is which.
 * 3. **The way to billing**, for somebody who can actually change the plan.
 * 4. **What to do when money is not the answer.** A module is held, not bought up a ladder, so
 *    `needed` is null and offering a plan that would not give them the thing is worse than
 *    offering nothing at all. It says so plainly and points at us instead.
 */
export function PricingModal({
  gate,
  open,
  onClose,
}: {
  gate: Gate;
  open: boolean;
  onClose: () => void;
}) {
  /* `mayReach`, not `can` — the billing screen is a screen, and a read-only owner still needs to
     be able to open it and see what their agency is paying for. */
  const { mayReach, modules } = useSession();
  /* Both destinations below are screens the two modules share, and this modal is opened from a
     padlock that can appear on any screen in either of them. It keeps the module it was pressed
     from — see `useModuleLink`. */
  const link = useModuleLink(modules);
  const buyable = gate.needed !== null;

  return (
    <Modal open={open} onClose={onClose} title={buyable ? "Locked on your plan" : "Not part of what you hold"}>
      <div data-pricing-modal={gate.reason} data-pricing-subject={gate.subject}>
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-full bg-brand-50 text-brand-700">
            <IconLock size={19} />
          </span>
          <p className="text-sm text-ink-700">{gate.message}</p>
        </div>

        {/*
          The two plans, side by side.

          A sentence saying "you are on Starter and this needs Growth" is read once and argued
          with; two boxes with an arrow between them is read at a glance and is the shape of the
          decision being offered.
        */}
        <div className="mt-4 flex items-stretch gap-2">
          <PlanChip label="You are on" name={gate.current.name} />
          {buyable ? (
            <>
              <span aria-hidden className="self-center text-ink-300">→</span>
              <PlanChip label="Opens on" name={gate.needed?.name ?? ""} highlight />
            </>
          ) : null}
        </div>

        {buyable ? (
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {mayReach("org.billing") ? (
              <Link to={link("/app/settings/billing")} onClick={onClose} className="btn btn-primary" data-padlock-see-plans>
                See the plans
              </Link>
            ) : (
              /* Not a dead link. Somebody without billing rights cannot open that screen at all —
                 sending them to a redirect would be the product refusing them twice. */
              <p className="text-sm text-ink-600" data-padlock-ask-billing>
                Whoever looks after billing for your agency can change this from Plan and billing.
              </p>
            )}
            <button type="button" onClick={onClose} className="btn btn-ghost">
              Not now
            </button>
          </div>
        ) : (
          <div className="mt-5">
            <p className="text-sm text-ink-600" data-padlock-not-for-sale>
              No plan opens this one. It is part of what your agency holds rather than a rung on the
              pricing ladder, so moving up would not add it — talk to us and we can.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Link to={link("/app/support")} onClick={onClose} className="btn btn-primary">
                Talk to us
              </Link>
              <button type="button" onClick={onClose} className="btn btn-ghost">
                Not now
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function PlanChip({ label, name, highlight = false }: { label: string; name: string; highlight?: boolean }) {
  return (
    <div
      className={`flex-1 rounded-[var(--radius-surface)] border px-3 py-2 ${
        highlight ? "border-brand-600 bg-brand-50" : "border-ink-200 bg-white"
      }`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{label}</p>
      <p className={`text-sm font-semibold ${highlight ? "text-brand-700" : "text-ink-800"}`}>{name}</p>
    </div>
  );
}

/**
 * The button somebody would have pressed, drawn shut.
 *
 * Used where the control is an action rather than a record: "Add a property" when the plan's
 * ceiling has been reached, an export when the plan does not include one. **The words stay the
 * words** — it still says what it would have done, because a button relabelled "Upgrade" has
 * stopped telling the reader what they are missing, and what they are missing is the entire
 * argument for paying.
 *
 * Only ever drawn in place of a control this person could otherwise have pressed. A button that
 * their role removed stays removed; see `useRecordLock` in `lib/gating.ts`.
 */
export function PadlockButton({
  gate,
  children,
  variant = "secondary",
  size = "md",
  className = "",
}: {
  gate: Gate | null | undefined;
  children: ReactNode;
  variant?: "primary" | "secondary";
  size?: "sm" | "md";
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!gate) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={gate.message}
        data-padlock={gate.reason}
        data-padlock-subject={gate.subject}
        className={`btn btn-${variant} ${size === "sm" ? "btn-sm" : ""} ${className}`}
      >
        <IconLock size={size === "sm" ? 14 : 16} />
        {children}
      </button>
      <PricingModal gate={gate} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/**
 * A whole option drawn shut — a row, a tile, a panel, whatever it wraps.
 *
 * Used where the thing being gated is bigger than a mark beside a name: an export somebody would
 * otherwise tick, an import they would otherwise start. The content stays exactly where it was and
 * keeps its own words — this dims it, stops it being pressable, and puts the padlock on top.
 *
 * **Dimmed rather than removed.** The reader has to be able to see what they are not getting, or
 * there is nothing to want. `aria-hidden` on the inert content and a single labelled button over
 * the whole thing is what stops a screen reader walking into a set of controls that do nothing.
 */
export function LockedOption({
  gate,
  label = "Locked",
  children,
  className = "",
}: {
  gate: Gate | null | undefined;
  label?: string;
  children: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!gate) return <>{children}</>;

  return (
    <div className={`relative ${className}`} data-locked-option={gate.subject}>
      <div aria-hidden className="pointer-events-none select-none opacity-45 grayscale">
        {children}
      </div>

      <button
        type="button"
        onClick={() => setOpen(true)}
        title={gate.message}
        aria-label={`Locked — ${gate.message}`}
        data-padlock={gate.reason}
        data-padlock-subject={gate.subject}
        className="absolute inset-0 flex items-center justify-end rounded-[var(--radius-surface)] px-3 transition-colors hover:bg-brand-50/40"
      >
        <span className="inline-flex items-center gap-1 rounded-full border border-brand-200 bg-brand-50 px-2 py-1 text-[11px] font-semibold text-brand-700">
          <IconLock size={13} />
          {label}
        </span>
      </button>

      <PricingModal gate={gate} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

/**
 * A refusal that came back from a write, drawn as the padlock rather than as an error.
 *
 * The API answers `402` with the same `Gate` shape on its body — see `assertUnlocked` in
 * `apps/api/src/plan.ts` — so a save that is refused because of a plan can offer the way out
 * instead of a red box saying no. Anything else that failed is not this component's business and
 * it draws nothing, leaving the ordinary error message to do its job.
 */
export function GateAlert({ error }: { error: unknown }) {
  const [open, setOpen] = useState(false);
  const gate = gateOf(error);
  if (!gate) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-[var(--radius-surface)] border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-800"
      data-gate-alert={gate.subject}
      role="status"
    >
      <span className="grid size-8 shrink-0 place-items-center rounded-full bg-white text-brand-700">
        <IconLock size={17} />
      </span>
      <p className="min-w-0 flex-1">{gate.message}</p>
      <button type="button" onClick={() => setOpen(true)} className="btn btn-primary btn-sm">
        {gate.needed ? "See the plans" : "Find out more"}
      </button>
      <PricingModal gate={gate} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

/**
 * Whatever a write came back with, drawn as whichever of the two marks it deserves.
 *
 * Every form in the product needs the same decision, and it is a decision rather than a component
 * only because both halves have to be drawn by the same hand. Wired half-way — a `GateAlert` added
 * above an untouched red box — a refused save says the same sentence twice in two tones, once as an
 * offer and once as a failure, which is worse than either on its own. Made here, it cannot be made
 * wrong on the next form somebody adds.
 *
 * A refusal that named a plan is the padlock and the way out of it. Everything else — a name
 * already taken, a tenancy still live, a server that fell over — is the ordinary red box, unchanged.
 */
export function Refusal({ error, message }: { error: unknown; message?: string | null }) {
  if (!error) return null;
  if (gateOf(error)) return <GateAlert error={error} />;
  return message ? <Alert tone="danger">{message}</Alert> : null;
}

/** The gate on a thrown `ApiError`, when the refusal carried one. Null for everything else. */
export function gateOf(error: unknown): Gate | null {
  const candidate = (error as { gate?: Gate } | null)?.gate;
  return candidate && typeof candidate.message === "string" && typeof candidate.reason === "string"
    ? candidate
    : null;
}
